import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { KeyRound, Mail, User, Lock, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RedeemAccessID() {
  const location = useLocation();
  const [form, setForm] = useState({ code: '', name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    if (code) setForm((f) => ({ ...f, code: code.trim().toUpperCase() }));
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
      };
      await axios.post(`${API}/auth/redeem-id`, payload);
      setSent(true);
      toast.success('Verification email sent. Please check your inbox.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to redeem access ID');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6" data-testid="redeem-sent-screen">
        <div className="max-w-md w-full bg-white p-8 rounded-xl border border-slate-200 shadow-sm text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center mb-4">
            <Mail className="w-7 h-7 text-orange-600" />
          </div>
          <h1 className="text-2xl font-serif font-bold text-slate-900 mb-2">Check your inbox</h1>
          <p className="text-slate-600 mb-6">
            We sent a verification link to <strong>{form.email}</strong>. Click the link to activate your account. The link is valid for 24 hours.
          </p>
          <Link to="/login" className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800" data-testid="redeem-go-login-btn">
            Back to login <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6 py-12" data-testid="redeem-page">
      <div className="max-w-md w-full">
        <Link to="/" className="text-2xl font-serif font-bold text-slate-900 mb-6 block text-center">The Case Verse</Link>
        <div className="bg-white p-8 rounded-xl border border-slate-200 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <KeyRound className="w-6 h-6 text-orange-600" />
            <h1 className="text-2xl font-serif font-bold text-slate-900">Redeem access ID</h1>
          </div>
          <p className="text-sm text-slate-500 mb-6">
            Your institute shared an access ID with you. Enter it below along with your details to activate your account.
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Access ID</label>
              <input
                type="text"
                required
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="TCV-STU-XXXXXX"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 font-mono text-sm"
                data-testid="redeem-code-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Full name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full pl-9 pr-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
                  data-testid="redeem-name-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full pl-9 pr-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
                  data-testid="redeem-email-input"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">You will log in with this email after verification.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Choose a password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="password"
                  required
                  minLength={6}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full pl-9 pr-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900"
                  data-testid="redeem-password-input"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full px-6 py-3 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 disabled:opacity-50 transition-all"
              data-testid="redeem-submit-btn"
            >
              {loading ? 'Sending verification email…' : 'Send verification email'}
            </button>
          </form>
          <p className="text-sm text-slate-500 text-center mt-6">
            Already activated? <Link to="/login" className="text-orange-600 hover:underline">Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
